/*
 * _customled-relay-finder.js  (Maranello M6.1 — redesigned)
 *
 * Interaction layer for the redesigned relay finder
 * (snippets/_customled-relay-finder.liquid). Vanilla JS, no jQuery.
 *
 * Three answer-zone states: landing (chips) / results (recommendation cards
 * grouped per relay, with Add to Cart) / zero-result recovery (ELFR-P promo).
 * Result pill at the input is the single aria-live region (announcements
 * debounced separately from filtering). Scroll management fires only on
 * discrete actions (submit / select change / chip), never per keystroke.
 * URL state via replaceState (?q=&make=) hydrated on load.
 *
 * The MATCHING ENGINE (normalize*, year handling, rowHasToken,
 * rowMatchesText) is ported UNCHANGED from the proven M1.10.x build —
 * do not "improve" it without re-running the Ninja 400 / EX400 / VTX /
 * GSXR / year-collision test set.
 *
 * Localized strings arrive via data-* attributes on #relay-compatibility-finder
 * and <template> content — never hardcoded here (FR/ES rule).
 */
(function () {
  "use strict";

  function init() {
    var finder = document.getElementById('relay-compatibility-finder');
    if (!finder) return;

    var form = document.getElementById('relay-finder-form');
    var searchInput = document.getElementById('relay-search-input');
    var currentMake = '';
    var clearButton = document.getElementById('relay-clear-filters');
    var pillText = document.getElementById('relay-pill-text');
    var pillWrap = document.getElementById('relay-result-pill');
    var pillJump = document.getElementById('relay-pill-jump');
    var zoneLanding = document.getElementById('relay-zone-landing');
    var zoneResults = document.getElementById('relay-zone-results');
    var zoneEmpty = document.getElementById('relay-zone-empty');
    var zoneEmptyHeading = document.getElementById('relay-zone-empty-heading');
    var noResults = document.getElementById('relay-no-results');
    var browseStatus = document.getElementById('relay-browse-status');
    var rows = Array.prototype.slice.call(finder.querySelectorAll('.relay-result-row'));
    var groups = Array.prototype.slice.call(finder.querySelectorAll('.relay-make-group'));
    var strings = {
      pillPrompt: pillText ? pillText.textContent : '',
      pillTyping: finder.getAttribute('data-str-typing') || '',
      pillMatchOne: finder.getAttribute('data-str-match-one') || '1 matching model — “{query}”',
      pillMatches: finder.getAttribute('data-str-matches') || '{count} matching models — “{query}”',
      pillMatchOneMake: finder.getAttribute('data-str-match-one-make') || '1 supported model — {make}',
      pillMatchesMake: finder.getAttribute('data-str-matches-make') || '{count} supported models — {make}',
      pillNoMatch: finder.getAttribute('data-str-nomatch') || 'No match for “{query}”',
      fitLine: finder.getAttribute('data-str-fitline') || 'Fits {bike}',
      fitLineMulti: finder.getAttribute('data-str-fitline-multi') || 'Fits these {count} matching models:',
      fitLineMany: finder.getAttribute('data-str-fitline-many') || 'Fits {count} matching models — such as:',
      moreModels: finder.getAttribute('data-str-more') || '+ {count} more — see the chart below',
      emptyHeading: finder.getAttribute('data-str-empty-heading') || 'No match for “{query}”'
    };

    /* ---------------- matching engine (ported verbatim) ---------------- */
    function normalizeWords(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
    function normalizeCompact(value) { return normalizeWords(value).replace(/\s+/g, ''); }
    function getQueryYears(rawValue) {
      var matches = String(rawValue || '').match(/\b(?:19|20)\d{2}\b/g) || [];
      var unique = [];
      matches.forEach(function (year) { if (unique.indexOf(year) === -1) unique.push(year); });
      return unique;
    }
    function getTextQuery(rawValue) { return normalizeWords(String(rawValue || '').replace(/\b(?:19|20)\d{2}\b/g, ' ')); }
    function rowMatchesYears(row, queryYears) {
      if (!queryYears.length) return true;
      var rowYears = ' ' + String(row.getAttribute('data-years') || '').replace(/\s+/g, ' ') + ' ';
      if (!rowYears.trim()) return false;
      return queryYears.every(function (year) { return rowYears.indexOf(' ' + year + ' ') !== -1; });
    }
    function rowHasToken(row, token) {
      var tokenCompact = normalizeCompact(token);
      if (!tokenCompact) return true;
      if (tokenCompact.length <= 3) {
        return (' ' + row._relayModelWords).indexOf(' ' + tokenCompact) !== -1;
      }
      if (row._relaySearchTokenMap[tokenCompact]) return true;
      return row._relaySearchCompact.indexOf(tokenCompact) !== -1;
    }
    function rowMatchesText(row, textQuery) {
      if (!textQuery) return true;
      var rowSearchWords = row._relaySearchWords;
      var rowSearchCompact = row._relaySearchCompact;
      var queryCompact = normalizeCompact(textQuery);
      if (!queryCompact) return true;
      if (queryCompact.length > 3 && rowSearchWords.indexOf(textQuery) !== -1) return true;
      if (queryCompact.length > 3 && rowSearchCompact.indexOf(queryCompact) !== -1) return true;
      var queryTokens = textQuery.split(' ').filter(function (token) { return token; });
      if (queryTokens.length && queryTokens.every(function (token) { return rowHasToken(row, token); })) return true;
      if (queryTokens.length > 1 && queryTokens.every(function (token) { return token.length > 1; })) {
        var reversedQueryCompact = queryTokens.slice().reverse().join('');
        if (reversedQueryCompact.length > 3 && rowSearchCompact.indexOf(reversedQueryCompact) !== -1) return true;
      }
      return false;
    }
    rows.forEach(function (row) {
      var rowSearch = row.getAttribute('data-search');
      var modelCell = row.querySelector('.relay-model');
      /* CUSTOMLED M7.1: the model cell may carry a ".relay-model-excl" span
         ("Does not fit: X"). That text must never reach the matcher (it would
         make the excluded model SEARCHABLE on this row) nor the card fit-line
         (it would read "Fits A ... Does not fit B"). Read the primary text only. */
      var modelPrimary = '';
      if (modelCell) {
        var mc = modelCell.cloneNode(true);
        var ex = mc.querySelector('.relay-model-excl');
        if (ex) ex.parentNode.removeChild(ex);
        modelPrimary = mc.textContent;
      }
      var modelSearch = [row.getAttribute('data-make') || '', modelPrimary].join(' ');
      row._relaySearchWords = normalizeWords(rowSearch);
      row._relaySearchCompact = normalizeCompact(rowSearch);
      row._relaySearchTokenMap = {};
      row._relaySearchWords.split(' ').forEach(function (token) { if (token) row._relaySearchTokenMap[token] = true; });
      row._relayModelWords = normalizeWords(modelSearch);
      row._relayModelText = modelPrimary.trim();
      /* CUSTOMLED M7.1: exclusion tokens (data-exclude="XG750A; Street Rod").
         A query naming an excluded variant SUPPRESSES the row instead of
         surfacing it as a confident single match (post-mortem: 2018 Street Rod
         XG750A matched the XG750 row and was the only result). */
      row._relayExcludeTokens = (row.getAttribute('data-exclude') || '')
        .split(';').map(function (s) { return normalizeWords(s).trim(); }).filter(Boolean);
      row._relayOem = (function () { var c = row.querySelector('.relay-oem'); return c ? c.textContent.trim() : ''; })();
      row._relayGroupOpenState = null;
    });

    /* ---------------- state + filtering ---------------- */
    var lastQueryKey = '';
    var hasScrolledThisQuery = false;
    var announceTimer = null;

    function fmt(tpl, vars) {
      return tpl.replace(/\{(\w+)\}/g, function (_, k) { return vars[k] != null ? vars[k] : ''; });
    }

    function computeMatches() {
      var rawQuery = searchInput.value;
      var textQuery = getTextQuery(rawQuery);
      var queryYears = getQueryYears(rawQuery);
      var make = currentMake;
      var matched = [];
      rows.forEach(function (row) {
        var makeMatch = !make || row.getAttribute('data-make') === make;
        var excluded = false;
        if (row._relayExcludeTokens.length && textQuery) {
          var q = normalizeWords(textQuery);
          for (var xi = 0; xi < row._relayExcludeTokens.length; xi++) {
            if (q.indexOf(row._relayExcludeTokens[xi]) !== -1) { excluded = true; break; }
          }
        }
        var isVisible = !excluded && makeMatch && rowMatchesText(row, textQuery) && rowMatchesYears(row, queryYears);
        row.classList.toggle('relay-hidden', !isVisible);
        if (isVisible) matched.push(row);
      });
      groups.forEach(function (group) {
        var visibleRows = group.querySelectorAll('.relay-result-row:not(.relay-hidden)').length;
        group.classList.toggle('relay-hidden', visibleRows === 0);
        var active = !!(rawQuery.trim() || make);
        var desired = active ? visibleRows > 0 : false;
        // only touch open when the desired state changed (don't fight manual toggles)
        var key = active + ':' + desired;
        if (group._relayOpenKey !== key) { group.open = desired; group._relayOpenKey = key; }
      });
      return matched;
    }

    function updateBrowseStatus(matchedCount, filtersActive) {
      if (!browseStatus) return;
      var total = rows.length;
      if (!filtersActive) {
        browseStatus.textContent = fmt(browseStatus.getAttribute('data-str-all') || 'All {total} supported models, grouped by make.', { total: total });
      } else {
        browseStatus.textContent = '';
        var txt = document.createTextNode(fmt(browseStatus.getAttribute('data-str-filtered') || 'Showing {count} of {total} models that match your search — ', { count: matchedCount, total: total }) + ' ');
        var clearLink = document.createElement('button');
        clearLink.type = 'button';
        clearLink.className = 'relay-status-clear';
        clearLink.textContent = browseStatus.getAttribute('data-str-clear') || 'clear filters to see all';
        clearLink.addEventListener('click', function () { clearButton.click(); });
        browseStatus.appendChild(txt);
        browseStatus.appendChild(clearLink);
      }
    }
    function setZone(state) {
      zoneLanding.hidden = state !== 'landing';
      zoneResults.hidden = state !== 'results';
      zoneEmpty.hidden = state !== 'empty';
    }

    function announce(text) {
      clearTimeout(announceTimer);
      announceTimer = setTimeout(function () { pillWrap.hidden = !text; pillText.textContent = text; }, 350);
    }
    function announceNow(text) { clearTimeout(announceTimer); pillWrap.hidden = !text; pillText.textContent = text; }

    function buildCards(matched, rawQuery) {
      zoneResults.innerHTML = '';
      var byRelay = {};
      var order = [];
      matched.forEach(function (row) {
        var sku = row.getAttribute('data-relay');
        if (!byRelay[sku]) { byRelay[sku] = []; order.push(sku); }
        byRelay[sku].push(row);
      });
      order.forEach(function (sku) {
        var tpl = document.getElementById('relay-card-tpl-' + sku);
        if (!tpl) return;
        var card = tpl.content.cloneNode(true);
        var list = byRelay[sku];
        var fit = card.querySelector('[data-slot="fitline"]');
        var rowsSlot = card.querySelector('[data-slot="rows"]');
        var max = 4;
        // adaptive fit line: precise for 1 match, enumerated for 2-4,
        // summary ("such as") for broad results — never implies the first
        // alphabetical row is a special recommendation (M6.6, Jon feedback)
        if (fit) {
          if (list.length === 1) fit.textContent = fmt(strings.fitLine, { bike: list[0]._relayModelText });
          else if (list.length <= max) fit.textContent = fmt(strings.fitLineMulti, { count: list.length });
          else fit.textContent = fmt(strings.fitLineMany, { count: list.length });
        }
        list.slice(0, max).forEach(function (row) {
          var line = document.createElement('p');
          line.className = 'relay-reccard__row';
          line.textContent = row._relayModelText + (row._relayOem ? ' · OEM: ' + row._relayOem : '');
          rowsSlot.appendChild(line);
        });
        if (list.length > max) {
          var more = document.createElement('a');
          more.className = 'relay-reccard__more';
          more.href = '#relay-results';
          more.textContent = fmt(strings.moreModels, { count: list.length - max });
          rowsSlot.appendChild(more);
        }
        zoneResults.appendChild(card);
      });
    }

    function refresh(opts) {
      opts = opts || {};
      var rawQuery = searchInput.value.trim();
      var make = currentMake;
      var queryKey = rawQuery + '|' + make;
      if (queryKey !== lastQueryKey) {
        hasScrolledThisQuery = false;
        lastQueryKey = queryKey;
        groups.forEach(function (g) { g._relayOpenKey = null; });
      }

      // URL state
      try {
        var params = new URLSearchParams(window.location.search);
        if (rawQuery) params.set('q', rawQuery); else params.delete('q');
        if (make) params.set('make', make); else params.delete('make');
        var qs = params.toString();
        history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
      } catch (e) { /* no-op */ }

      if (!rawQuery && !make) {
        computeMatches();
        zoneResults.innerHTML = '';
        setZone('landing');
        if (noResults) noResults.hidden = true;
        pillJump.hidden = true;
        updateBrowseStatus(0, false);
        announceNow('');
        return;
      }
      if (rawQuery && rawQuery.length < 2 && !make) {
        // keep-typing: reset the browse table to the unfiltered state so the
        // chart below never carries a stale previous-query filter (M6.2 fix)
        rows.forEach(function (row) { row.classList.remove('relay-hidden'); });
        groups.forEach(function (g) { g.classList.remove('relay-hidden'); g.open = false; g._relayOpenKey = null; });
        if (noResults) noResults.hidden = true;
        zoneResults.innerHTML = '';
        setZone('landing');
        pillJump.hidden = true;
        updateBrowseStatus(0, false);
        announce(strings.pillTyping);
        return;
      }
      var matched = computeMatches();
      updateBrowseStatus(matched.length, true);
      if (matched.length === 0) {
        zoneResults.innerHTML = '';
        setZone('empty');
        if (zoneEmptyHeading) zoneEmptyHeading.textContent = fmt(strings.emptyHeading, { query: rawQuery || make });
        if (noResults) noResults.hidden = false;
        pillJump.hidden = true;
        announce(fmt(strings.pillNoMatch, { query: rawQuery || make }));
      } else {
        buildCards(matched, rawQuery);
        setZone('results');
        if (noResults) noResults.hidden = true;
        pillJump.hidden = false;
        var msg = rawQuery
          ? fmt(matched.length === 1 ? strings.pillMatchOne : strings.pillMatches, { count: matched.length, query: rawQuery })
          : fmt(matched.length === 1 ? strings.pillMatchOneMake : strings.pillMatchesMake, { count: matched.length, make: make });
        announce(msg);
      }
      if (opts.scroll && !hasScrolledThisQuery) {
        var zoneRect = document.getElementById('relay-answer-zone').getBoundingClientRect();
        if (zoneRect.top > window.innerHeight * 0.6 || zoneRect.top < 0) {
          form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        hasScrolledThisQuery = true;
      }
    }

    /* ---------------- events ---------------- */
    var inputTimer = null;
    searchInput.addEventListener('input', function () {
      clearTimeout(inputTimer);
      inputTimer = setTimeout(function () { refresh({ scroll: false }); }, 180);
    });
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearTimeout(inputTimer);
      refresh({ scroll: true });
    });

    finder.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-chip-make]');
      if (chip) {
        currentMake = chip.getAttribute('data-chip-make');
        refresh({ scroll: true });
        var zone = document.getElementById('relay-answer-zone');
        if (zone) zone.focus({ preventScroll: true });
        return;
      }
    });
    clearButton.addEventListener('click', function () {
      searchInput.value = '';
      currentMake = '';
      groups.forEach(function (g) { g.open = false; g._relayOpenKey = null; });
      refresh({ scroll: false });
      searchInput.focus();
    });

    // hydrate from URL
    try {
      var boot = new URLSearchParams(window.location.search);
      var q0 = boot.get('q') || '';
      var m0 = boot.get('make') || '';
      if (q0) searchInput.value = q0;
      if (m0) currentMake = m0;
      refresh({ scroll: !!(q0 || m0) });
    } catch (e) { refresh({ scroll: false }); }

    /* ------------- note popovers: one at a time (unchanged) ------------- */
    document.addEventListener('toggle', function (event) {
      var target = event.target;
      if (!target || !target.classList || !target.classList.contains('relay-note-popover')) return;
      if (!target.open) return;
      var others = document.querySelectorAll('.relay-note-popover[open]');
      others.forEach(function (other) { if (other !== target) other.open = false; });
    }, true);

    /* ------------- guidance lightbox (a11y-fixed) ------------- */
    var LIGHTBOX_LINK_PATTERN = /ELFR-1-QD_Self-Cancel_Relay_Hookup|Replacement_Flasher_Relay_Connected/;
    var MODAL_ID = 'customled-guidance-modal';
    var guidanceLabel = finder.getAttribute('data-guidance-label') || 'Installation guidance';
    var closeLabel = finder.getAttribute('data-close-label') || 'Close';

    function getOrCreateGuidanceModal() {
      var existing = document.getElementById(MODAL_ID);
      if (existing) return existing;
      var tmp = document.createElement('div');
      tmp.innerHTML =
        '<modal-dialog id="' + MODAL_ID + '" class="media-modal customled-guidance-modal">' +
          '<div role="dialog" aria-modal="true" aria-label="' + guidanceLabel.replace(/"/g, '&quot;') + '" tabindex="-1">' +
            '<button id="ModalClose-' + MODAL_ID + '" type="button" class="customled-guidance-modal__close" aria-label="' + closeLabel.replace(/"/g, '&quot;') + '">' +
              '<svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
                '<path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>' +
              '</svg>' +
            '</button>' +
            '<img class="customled-guidance-modal__img" src="" alt="">' +
          '</div>' +
        '</modal-dialog>';
      var modal = tmp.firstChild;
      document.body.appendChild(modal);
      return modal;
    }
    finder.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (!link) return;
      if (!LIGHTBOX_LINK_PATTERN.test(link.href)) return;
      event.preventDefault();
      var modal = getOrCreateGuidanceModal();
      var img = modal.querySelector('.customled-guidance-modal__img');
      img.src = link.href;
      img.alt = (link.textContent || '').trim() || guidanceLabel;
      if (typeof modal.show === 'function') {
        modal.show(link);
      } else {
        requestAnimationFrame(function () {
          if (typeof modal.show === 'function') modal.show(link);
          else window.location.href = link.href; // never die silently
        });
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
